import axios from 'axios';
import { t } from 'i18next';
import urlTools from './urlTools';
import { decryptJSAesData } from './decryptTools';
import { getSelectedPacsConfig } from './pacsSelectorConfig';
import updateConfig from './updateConfig';
import {
  beginStartupCheck,
  enableStartupStatusFeedback,
  finishStartupCheck,
} from './startupValidation';

/* global globalAppConfig */

export { addSubdirectoryToPath } from './updateConfig';

const isConfigObject = (config) => Boolean(config && typeof config === 'object' && !Array.isArray(config));
const skipLicenseVerification = process.env.SKIP_VERIFY_LICENSE === 'yes';

const registerFollowUpChecks = (appConfig) => {
  const securityRequests = [
    appConfig.HAS_REMOTE_APP_CONFIG,
    !skipLicenseVerification && appConfig.HAS_ENCRYPTED_APP_CONFIG,
  ].filter(Boolean);
  securityRequests.forEach(() => beginStartupCheck('securityConfig', 'Encryption parameters'));
  if (appConfig.HAS_LICENSE_CONFIG) beginStartupCheck('securityConfig', 'Encryption parameters');
  enableStartupStatusFeedback();
};

const APP_CONFIG_LOCATION = process.env.APP_CONFIG_LOCATION;

const currentUrlInfo = urlTools.getCurrentUrlInfo();

export const getPacsEndpoints = (config) => {
  const selectedPacs = getSelectedPacsConfig(config);

  return {
    wadoRsEndpoint: selectedPacs?.wadoRsUrl || config.WADO_RS_ENDPOINT,
    qidoRsEndpoint: selectedPacs?.qidoRsUrl || config.QIDO_RS_ENDPOINT,
    wadoUriEndpoint: selectedPacs?.wadoUriUrl || config.WADO_URI_ENDPOINT,
    wadoUriEnabled: config.WEB_SERVICE_TYPE === 'WADO-URI',
  };
};

export const getAuthenticationConfig = (config) => {
  const openIdConnect = config.OPEN_ID_CONNECT;
  const hasOpenIdConnect = Boolean(
    openIdConnect
      && typeof openIdConnect === 'object'
      && Object.keys(openIdConnect).length > 0
  );

  return {
    oidcEnabled: Boolean(config.CLOUD_WITH_AUTH || hasOpenIdConnect),
    authUrl: openIdConnect?.AUTH_URL || openIdConnect?.KEYCLOAK_URL || config.KEYCLOAK_URL,
    realm: openIdConnect?.REALM || openIdConnect?.KEYCLOAK_REALM || config.KEYCLOAK_REALM,
    clientId: openIdConnect?.CLIENT_ID || openIdConnect?.KEYCLOAK_CLIENT_ID || config.KEYCLOAK_CLIENT_ID,
  };
};

const getMultiDomainUrl = (appConfig) => {
  const appConfigUrl = currentUrlInfo.protocol === 'https' ? appConfig.REMOTE_APP_CONFIG_URL : appConfig.INTRANET_APP_CONFIG_URL;

  return urlTools.isAbsoluteUrl(appConfigUrl) ? appConfigUrl : urlTools.completeOrRebuildUrl(appConfigUrl);
};

const getRemoteAppConfig = async function (appConfig) {
  let remoteAppConfigUrl;
  if (appConfig.INTRANET_APP_CONFIG_URL) {
    remoteAppConfigUrl = getMultiDomainUrl(appConfig);
  } else {
    remoteAppConfigUrl = appConfig.REMOTE_APP_CONFIG_URL;
  }

  let remoteAppConfig;
  try {
    const postData = { LICENSE_TOKEN: appConfig.LICENSE_TOKEN };
    const response = await axios.post(remoteAppConfigUrl, postData);
    if (response.status === 200) {
      remoteAppConfig = response.data;
    } else {
      console.error(`*** error unhandled Http code: ${response.status}`);
      return undefined;
    }
  } catch (error) {
    console.error('*** ERROR GET REMOTE APP CONFIG. Error message: ', error, `\n\nurl: ${remoteAppConfigUrl}`, '\ntoken:', appConfig.LICENSE_TOKEN);
  }

  return remoteAppConfig;
};

const getEncryptedAppConfig = async function (appConfig) {
  if (skipLicenseVerification) return undefined;

  let encryptedAppConfig;
  try {
    const response = await axios.get(appConfig.ENCRYPTED_APP_CONFIG_URL);
    if (response.status === 200) {
      encryptedAppConfig = decryptJSAesData(response.data);
    } else {
      console.error(`*** error unhandled Http code: ${response.status}`);
      return undefined;
    }
  } catch (error) {
    console.error('*** ERROR GET ENCRYPTED APP CONFIG. Error message: ', error, `\n\nurl: ${appConfig.ENCRYPTED_APP_CONFIG_URL}`, '\ntoken:');
  }

  return encryptedAppConfig;
};

export const getAppConfig = async () => {
  // allow load dynamic appConfig from index.html creating globalAppConfig variable
  if (typeof globalAppConfig !== 'undefined') {
    beginStartupCheck('applicationConfig', 'Application configuration');
    const appConfig = globalAppConfig;
    finishStartupCheck('applicationConfig', isConfigObject(appConfig));
    if (isConfigObject(appConfig)) {
      registerFollowUpChecks(appConfig);
    } else {
      enableStartupStatusFeedback();
    }
    return appConfig;
  }

  let appConfig;
  try {
    appConfig = await updateConfig(APP_CONFIG_LOCATION);
  } catch (error) {
    enableStartupStatusFeedback();
    throw error;
  }
  if (!appConfig) {
    enableStartupStatusFeedback();
    return undefined;
  }

  registerFollowUpChecks(appConfig);

  if (appConfig.HAS_REMOTE_APP_CONFIG) {
    let remoteAppConfig;
    try {
      remoteAppConfig = await getRemoteAppConfig(appConfig);
    } finally {
      finishStartupCheck('securityConfig', isConfigObject(remoteAppConfig));
    }
    if (remoteAppConfig) {
      appConfig = { ...appConfig, ...remoteAppConfig };
    } else {
      console.error('Warning: Remote Config was activated, but couldnt fetch any config.');
      throw new Error(t('license.verificationError'));
    }
  }

  if (appConfig.HAS_ENCRYPTED_APP_CONFIG && !skipLicenseVerification) {
    let encryptedAppConfig;
    try {
      encryptedAppConfig = await getEncryptedAppConfig(appConfig);
    } finally {
      finishStartupCheck('securityConfig', isConfigObject(encryptedAppConfig));
    }
    if (encryptedAppConfig) {
      appConfig = { ...appConfig, ...encryptedAppConfig };
    } else {
      console.error('Warning: Encrypted Config was activated, but couldnt fetch any config.');
      throw new Error(t('license.verificationError'));
    }
  }

  return appConfig;
};
